import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { BreadcrumbSchema } from "@/components/BreadcrumbSchema";

// Eagerly load the homepage for best LCP
import Index from "./pages/Index";

// Lazy-load all other pages to reduce initial bundle
const Emergency = lazy(() => import("./pages/Emergency"));
const WaterDamage = lazy(() => import("./pages/WaterDamage"));
const OurWork = lazy(() => import("./pages/OurWork"));
const About = lazy(() => import("./pages/About"));
const Company = lazy(() => import("./pages/Company"));
const Emergencia = lazy(() => import("./pages/Emergencia"));
const Contact = lazy(() => import("./pages/Contact"));
const Services = lazy(() => import("./pages/Services"));
const Technology = lazy(() => import("./pages/Technology"));
const NotFound = lazy(() => import("./pages/NotFound"));
const WaterExtraction = lazy(() => import("./pages/WaterExtraction"));
const SewageCleanup = lazy(() => import("./pages/SewageCleanup"));
const StormDamage = lazy(() => import("./pages/StormDamage"));
const BurstPipes = lazy(() => import("./pages/BurstPipes"));
const MoldRemediation = lazy(() => import("./pages/MoldRemediation"));
const SanMarcos = lazy(() => import("./pages/SanMarcos"));
const Seguin = lazy(() => import("./pages/Seguin"));
const CanyonLake = lazy(() => import("./pages/CanyonLake"));
const Reconstruction = lazy(() => import("./pages/Reconstruction"));
const StructuralDrying = lazy(() => import("./pages/StructuralDrying"));
const ContentsPackOut = lazy(() => import("./pages/ContentsPackOut"));
const InsuranceClaims = lazy(() => import("./pages/InsuranceClaims"));
const NewBraunfels = lazy(() => import("./pages/NewBraunfels"));
const Areas = lazy(() => import("./pages/Areas"));
const BambooCaseStudy = lazy(() => import("./pages/BambooCaseStudy"));
const MoistureSurvey = lazy(() => import("./pages/MoistureSurvey"));
const Category3ToiletBackup = lazy(() => import("./pages/Category3ToiletBackup"));
const SewerBackupCaseStudy = lazy(() => import("./pages/SewerBackupCaseStudy"));
const ElksLodgeCaseStudy = lazy(() => import("./pages/ElksLodgeCaseStudy"));
const EmergencyWaterDamage = lazy(() => import("./pages/EmergencyWaterDamage"));
const FreezeDamage = lazy(() => import("./pages/FreezeDamage"));
const FreezeDamagePPC = lazy(() => import("./pages/FreezeDamagePPC"));
const WaterDamagePPC = lazy(() => import("./pages/WaterDamagePPC"));
const SewageCleanupPPC = lazy(() => import("./pages/SewageCleanupPPC"));
const BurstPipesPPC = lazy(() => import("./pages/BurstPipesPPC"));
const Sitemap = lazy(() => import("./pages/Sitemap"));
const Search = lazy(() => import("./pages/Search"));
const DeepDive = lazy(() => import("./pages/DeepDive"));
const EmergencyWaterDamageMitigationCentralTexas = lazy(() => import("./pages/EmergencyWaterDamageMitigationCentralTexas"));
const GainesvilleMetalBuildingInspection = lazy(() => import("./pages/GainesvilleMetalBuildingInspection"));
const SurvivalGuide = lazy(() => import("./pages/SurvivalGuide"));
const KnowledgeBase = lazy(() => import("./pages/KnowledgeBase"));
const Process = lazy(() => import("./pages/Process"));
const WhyWTR = lazy(() => import("./pages/WhyWTR"));
const ConcertoDrCaseStudy = lazy(() => import("./pages/ConcertoDrCaseStudy"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const HowToRecoverFromWaterDamage = lazy(() => import("./pages/HowToRecoverFromWaterDamage"));
const HowToTellIfWaterDamageIsSerious = lazy(() => import("./pages/HowToTellIfWaterDamageIsSerious"));
const HowToRemoveWaterDamage = lazy(() => import("./pages/HowToRemoveWaterDamage"));
const WhatDoesWaterDamageLookLike = lazy(() => import("./pages/WhatDoesWaterDamageLookLike"));
const SleepingInWaterDamagedHome = lazy(() => import("./pages/SleepingInWaterDamagedHome"));
const FlashFloodDamage = lazy(() => import("./pages/FlashFloodDamage"));
const Blog = lazy(() => import("./pages/Blog"));
const IsWaterDamagePermanent = lazy(() => import("./pages/IsWaterDamagePermanent"));
const EffectsOfWaterDamage = lazy(() => import("./pages/EffectsOfWaterDamage"));
const FireStation7CaseStudy = lazy(() => import("./pages/FireStation7CaseStudy"));
const InsuranceMythsBlog = lazy(() => import("./pages/InsuranceMythsBlog"));
const ScienceOfRestoration = lazy(() => import("./pages/ScienceOfRestoration"));
const MultiUnitWaterDamage = lazy(() => import("./pages/MultiUnitWaterDamage"));
const HistoricHomeRestoration = lazy(() => import("./pages/HistoricHomeRestoration"));
const MoldRemediationHealth = lazy(() => import("./pages/MoldRemediationHealth"));
const GuadalupeFloodPrep = lazy(() => import("./pages/GuadalupeFloodPrep"));
const InsuranceClaimTips = lazy(() => import("./pages/InsuranceClaimTips"));
const DamageAssessmentContact = lazy(() => import("./pages/DamageAssessmentContact"));
const Connect = lazy(() => import("./pages/Connect"));
const WaterInWalls = lazy(() => import("./pages/WaterInWalls"));
const HowMuchDamageCanWaterDo = lazy(() => import("./pages/HowMuchDamageCanWaterDo"));
const IsWaterDamageRepairable = lazy(() => import("./pages/IsWaterDamageRepairable"));
const EssentialPreventionTips = lazy(() => import("./pages/blog/EssentialPreventionTips"));
const SuccessfulPreventionGuide = lazy(() => import("./pages/blog/SuccessfulPreventionGuide"));
const AvoidWaterDamageMistakes = lazy(() => import("./pages/blog/AvoidWaterDamageMistakes"));
const AvoidCleanupMistakes = lazy(() => import("./pages/blog/AvoidCleanupMistakes"));
const StepsWaterDamageHome = lazy(() => import("./pages/blog/StepsWaterDamageHome"));
const WhatToDoAfterWaterDamage = lazy(() => import("./pages/blog/WhatToDoAfterWaterDamage"));
const WhatNotToDoAfterWaterDamage = lazy(() => import("./pages/blog/WhatNotToDoAfterWaterDamage"));
const EmergencyWaterExtractionTexas = lazy(() => import("./pages/blog/EmergencyWaterExtractionTexas"));
const NeedWaterCleanUpTexas = lazy(() => import("./pages/blog/NeedWaterCleanUpTexas"));
const WhatIsWaterDamageRestoration = lazy(() => import("./pages/blog/WhatIsWaterDamageRestoration"));
const ManagedBlogPost = lazy(() => import("./pages/ManagedBlogPost"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wtr-blue"></div>
  </div>
);

interface AppProps {
  /** Provided only by the build-time prerenderer, which supplies its own router. */
  Router?: React.ComponentType<{ children: React.ReactNode }>;
}

const App = ({ Router = BrowserRouter as unknown as React.ComponentType<{ children: React.ReactNode }> }: AppProps = {}) => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <Router>
        <BreadcrumbSchema />
        <Suspense fallback={<PageLoader />}>
          <Routes>

            {/* Tier 1: Authority Pages */}
            <Route path="/" element={<Index />} />
            <Route path="/services" element={<Services />} />
            <Route path="/emergency" element={<Emergency />} />
            <Route path="/emergencia" element={<Emergencia />} />
            <Route path="/es" element={<Emergencia />} />
            <Route path="/areas" element={<Areas />} />
            <Route path="/technology" element={<Technology />} />
            <Route path="/insurance" element={<InsuranceClaims />} />
            <Route path="/our-work" element={<OurWork />} />
            <Route path="/about" element={<About />} />
            <Route path="/company" element={<Company />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/process" element={<Process />} />
            <Route path="/blog" element={<Blog />} />

            {/* Tier 2: Service Silos */}
            <Route path="/water-damage" element={<WaterDamage />} />
            <Route path="/sewage-cleanup" element={<SewageCleanup />} />
            <Route path="/mold-remediation" element={<MoldRemediation />} />
            <Route path="/storm-damage" element={<StormDamage />} />
            <Route path="/freeze-damage" element={<FreezeDamage />} />
            <Route path="/structural-drying" element={<StructuralDrying />} />
            <Route path="/water-extraction" element={<WaterExtraction />} />
            <Route path="/reconstruction" element={<Reconstruction />} />
            <Route path="/contents-pack-out" element={<ContentsPackOut />} />
            <Route path="/moisture-survey" element={<MoistureSurvey />} />
            <Route path="/burst-pipes" element={<BurstPipes />} />

            {/* SEO pages (not in nav) */}
            <Route path="/emergency-water-damage" element={<EmergencyWaterDamage />} />
            <Route path="/emergency-water-damage-mitigation-central-texas" element={<EmergencyWaterDamageMitigationCentralTexas />} />

            {/* Tier 3: Location Pages */}
            <Route path="/new-braunfels" element={<NewBraunfels />} />
            <Route path="/san-marcos" element={<SanMarcos />} />
            <Route path="/seguin" element={<Seguin />} />
            <Route path="/canyon-lake" element={<CanyonLake />} />

            {/* PPC Landing Pages (isolated funnels) */}
            <Route path="/lp/freeze-damage" element={<FreezeDamagePPC />} />
            <Route path="/lp/water-damage" element={<WaterDamagePPC />} />
            <Route path="/lp/sewage-cleanup" element={<SewageCleanupPPC />} />
            <Route path="/lp/burst-pipes" element={<BurstPipesPPC />} />

            {/* Case Studies */}
            <Route path="/bamboo-flooring-water-damage-full-replacement" element={<BambooCaseStudy />} />
            <Route path="/category-3-toilet-backup-restoration" element={<Category3ToiletBackup />} />
            <Route path="/sewer-backup-full-home-restoration" element={<SewerBackupCaseStudy />} />
            <Route path="/elks-lodge-seguin-restoration" element={<ElksLodgeCaseStudy />} />
            <Route path="/gainesville-metal-building-structural-inspection" element={<GainesvilleMetalBuildingInspection />} />
            <Route path="/refrigerator-line-flood-12-room-mitigation" element={<ConcertoDrCaseStudy />} />
            <Route path="/60-minute-water-damage-survival-guide" element={<SurvivalGuide />} />
            <Route path="/knowledge-base" element={<KnowledgeBase />} />
            <Route path="/how-to-recover-from-water-damage" element={<HowToRecoverFromWaterDamage />} />
            <Route path="/how-to-tell-if-water-damage-is-serious" element={<HowToTellIfWaterDamageIsSerious />} />
            <Route path="/how-to-remove-water-damage-from-a-house" element={<HowToRemoveWaterDamage />} />
            <Route path="/what-does-water-damage-look-like" element={<WhatDoesWaterDamageLookLike />} />
            <Route path="/sleeping-in-water-damaged-home" element={<SleepingInWaterDamagedHome />} />
            <Route path="/is-water-damage-permanent" element={<IsWaterDamagePermanent />} />
            <Route path="/effects-of-water-damage" element={<EffectsOfWaterDamage />} />
            <Route path="/hvac-leak-fire-station-7-new-braunfels" element={<FireStation7CaseStudy />} />
            <Route path="/water-damage-insurance-claim-myths" element={<InsuranceMythsBlog />} />
            <Route path="/science-of-water-damage-restoration" element={<ScienceOfRestoration />} />
            <Route path="/water-damage-condos-apartments" element={<MultiUnitWaterDamage />} />
            <Route path="/historic-home-water-damage-restoration" element={<HistoricHomeRestoration />} />
            <Route path="/mold-remediation-after-water-damage" element={<MoldRemediationHealth />} />
            <Route path="/guadalupe-river-flood-preparedness" element={<GuadalupeFloodPrep />} />
            <Route path="/flash-flood-damage" element={<FlashFloodDamage />} />
            <Route path="/insurance-tips-water-damage-claims" element={<InsuranceClaimTips />} />
            <Route path="/what-happens-if-water-gets-in-your-walls" element={<WaterInWalls />} />
            <Route path="/how-much-damage-can-water-do-to-a-house" element={<HowMuchDamageCanWaterDo />} />
            <Route path="/is-water-damage-repairable" element={<IsWaterDamageRepairable />} />
            <Route path="/essential-water-damage-prevention-tips-for-homeowners" element={<EssentialPreventionTips />} />
            <Route path="/your-guide-to-successful-water-damage-prevention-tips" element={<SuccessfulPreventionGuide />} />
            <Route path="/avoid-these-water-damage-mistakes" element={<AvoidWaterDamageMistakes />} />
            <Route path="/avoid-these-mistakes-with-water-damage-cleanup" element={<AvoidCleanupMistakes />} />
            <Route path="/steps-to-take-when-your-home-is-affected-by-water-damage" element={<StepsWaterDamageHome />} />
            <Route path="/what-to-do-immediately-after-water-damage" element={<WhatToDoAfterWaterDamage />} />
            <Route path="/what-not-to-do-after-water-damage" element={<WhatNotToDoAfterWaterDamage />} />
            <Route path="/emergency-water-extraction-texas-quick-reliable-services" element={<EmergencyWaterExtractionTexas />} />
            <Route path="/need-water-clean-up-in-texas" element={<NeedWaterCleanUpTexas />} />
            <Route path="/what-is-water-damage-restoration" element={<WhatIsWaterDamageRestoration />} />
            <Route path="/blog/:slug" element={<ManagedBlogPost />} />

            {/* Form Confirmation Page (noindex) */}
            <Route path="/damage-assessment-contact" element={<DamageAssessmentContact />} />

            {/* Authority Pages */}
            <Route path="/why-wtr" element={<WhyWTR />} />

            {/* Utility */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/sitemap" element={<Sitemap />} />
            <Route path="/search" element={<Search />} />
            <Route path="/deep-dive" element={<DeepDive />} />
            <Route path="/connect" element={<Connect />} />

            {/* 301-style Redirects: Consolidate duplicates */}
            <Route path="/mold" element={<Navigate to="/mold-remediation" replace />} />
            <Route path="/sewage" element={<Navigate to="/sewage-cleanup" replace />} />
            <Route path="/frozen-pipes" element={<Navigate to="/freeze-damage" replace />} />
            <Route path="/pipe-burst" element={<Navigate to="/freeze-damage" replace />} />
            <Route path="/insurance-claims" element={<Navigate to="/insurance" replace />} />
            <Route path="/resources" element={<Navigate to="/our-work" replace />} />
            <Route path="/landing" element={<Navigate to="/" replace />} />
            <Route path="/bamboo-case-study" element={<Navigate to="/bamboo-flooring-water-damage-full-replacement" replace />} />
            <Route path="/category-3-toilet-backup" element={<Navigate to="/category-3-toilet-backup-restoration" replace />} />
            <Route path="/sewer-backup-case-study" element={<Navigate to="/sewer-backup-full-home-restoration" replace />} />

            {/* Catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Router>
    </TooltipProvider>

  </QueryClientProvider>
);

export default App;
